import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '@/context';
import type { Role } from '@/types';

interface ProtectedRouteProps {
  /** Optional legacy children — use when wrapping non-Outlet routes. */
  children?: React.ReactNode;
  /** If provided, user.role must be in this list or a 403 page is shown. */
  requiredRoles?: Role[];
  /** @deprecated Use requiredRoles instead */
  allowedRoles?: string[];
}

// -----------------------------------------------------------------------
// Styled
// -----------------------------------------------------------------------

const LoadingWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.background};
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid ${({ theme }) => theme.colors.border};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ForbiddenWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
`;

const ForbiddenTitle = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamilyHeading};
  font-size: ${({ theme }) => theme.typography.fontSizes['4xl']};
  color: ${({ theme }) => theme.colors.error};
  margin: 0;
`;

// -----------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles,
  allowedRoles,
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <LoadingWrapper data-testid="auth-loading">
        <LoadingSpinner />
      </LoadingWrapper>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Merge both role props for backward compat.
  const roles = requiredRoles ?? (allowedRoles as Role[] | undefined);
  // owner is superuser — bypasses every role gate (mirrors backend require_role)
  if (roles && roles.length > 0 && user.role !== 'owner' && !roles.includes(user.role as Role)) {
    return (
      <ForbiddenWrapper data-testid="forbidden-page">
        <ForbiddenTitle>403</ForbiddenTitle>
        <p>You don&apos;t have permission to access this page.</p>
        <p>
          Your role: <strong>{user.role}</strong> — required:{' '}
          <strong>{roles.join(', ')}</strong>
        </p>
      </ForbiddenWrapper>
    );
  }

  // Supports both <ProtectedRoute><Component /></ProtectedRoute> and
  // router-level <Outlet /> usage.
  return <>{children ?? <Outlet />}</>;
};

export default ProtectedRoute;
